import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger
} from '@radix-ui/react-collapsible'

import { getOfferDuration } from '@/entities/offer/utils/getOfferDuration'
import { assert } from '@/shared/lib/assert'
import { Separator } from '@/shared/ui/Separator'

import { OfferForShowPrice } from '../types/OfferForShowPrice'
import { getDiscountValueForShow } from '../utils/getDiscountValueForShow'
import * as Elements from './OfferPriceContentElements'

interface OfferPriceContentProps {
	offer: OfferForShowPrice
}

export const OfferPriceContent = ({ offer }: OfferPriceContentProps) => {
	const price = offer.price
	const duration = getOfferDuration(offer)

	assert(duration !== null, 'Offer duration is null')

	const { t } = useTranslation('price')
	const { t: tTranslation } = useTranslation('translation', {
		keyPrefix: 'price'
	})
	const tValue = (value: string | number) =>
		tTranslation('base', { value: value })

	const [isAdditionalServicesOpen, setAdditionalServicesOpen] = useState(false)
	const [isPayAtCheckInOpen, setPayAtCheckInOpen] = useState(false)

	const detailedOfferPrice = price.detailed(false, true)

	return (
		<div className="flex flex-col gap-y-3">
			<Elements.Block aria-label={t('package.label')}>
				<Elements.Item
					className="ts-5"
					title={t('package.title')}
					value={tValue(detailedOfferPrice.package)}
				/>
			</Elements.Block>

			<Elements.Block aria-label={t('additionalServices.label')}>
				{detailedOfferPrice.additionalServices.length > 0 ? (
					<Collapsible
						open={isAdditionalServicesOpen}
						onOpenChange={setAdditionalServicesOpen}
					>
						<CollapsibleTrigger asChild>
							<Elements.Item
								className="ts-5 cursor-pointer"
								title={
									<Elements.CollapsibleTitle
										title={t('additionalServices.title')}
										isOpen={isAdditionalServicesOpen}
									/>
								}
								value={tValue(price.additionalServicesPrice())}
							/>
						</CollapsibleTrigger>

						<CollapsibleContent asChild>
							<Elements.Block aria-label={t('additionalServices.items.label')}>
								{detailedOfferPrice.additionalServices.map(
									additionalService => (
										<Elements.Item
											key={additionalService.name}
											title={additionalService.name}
											value={tValue(additionalService.amount)}
										/>
									)
								)}
							</Elements.Block>
						</CollapsibleContent>
					</Collapsible>
				) : (
					<Elements.Item
						className="ts-5"
						title={t('additionalServices.title')}
						value={tValue(price.additionalServicesPrice())}
					/>
				)}
			</Elements.Block>

			<Elements.Block aria-label={t('payAtCheckIn.label')}>
				{detailedOfferPrice.payAtCheckInServices.length > 0 ? (
					<Collapsible
						open={isPayAtCheckInOpen}
						onOpenChange={setPayAtCheckInOpen}
					>
						<CollapsibleTrigger asChild>
							<Elements.Item
								className="ts-5 cursor-pointer"
								title={
									<Elements.CollapsibleTitle
										title={t('payAtCheckIn.title')}
										isOpen={isPayAtCheckInOpen}
									/>
								}
								value={tValue(price.payAtCheckInServicesPrice())}
							/>
						</CollapsibleTrigger>

						<CollapsibleContent asChild>
							<Elements.Block aria-label={t('payAtCheckIn.items.label')}>
								{detailedOfferPrice.payAtCheckInServices.map(payAtCheckIn => (
									<Elements.Item
										key={payAtCheckIn.name}
										title={payAtCheckIn.name}
										value={tValue(payAtCheckIn.amount)}
									/>
								))}
							</Elements.Block>
						</CollapsibleContent>
					</Collapsible>
				) : (
					<Elements.Item
						className="ts-5"
						title={t('payAtCheckIn.title')}
						value={tValue(price.payAtCheckInServicesPrice())}
					/>
				)}
			</Elements.Block>

			{detailedOfferPrice.includedTax.length > 0 && (
				<Elements.Block aria-label={t('taxIncluded.label')}>
					<Elements.Item
						className="ts-5"
						title={t('taxIncluded.title')}
						value={tValue(price.includedTaxPrice())}
					/>

					{detailedOfferPrice.includedTax.map(tax => (
						<Elements.Item
							key={tax.name}
							title={tax.name}
							value={tValue(tax.amount)}
						/>
					))}
				</Elements.Block>
			)}

			{detailedOfferPrice.notIncludedTax.length > 0 && (
				<Elements.Block aria-label={t('taxNotIncluded.label')}>
					<Elements.Item
						className="ts-5"
						title={t('taxNotIncluded.title')}
						value={tValue(price.notIncludedTaxPrice())}
					/>

					{detailedOfferPrice.notIncludedTax.map(tax => (
						<Elements.Item
							key={tax.name}
							title={tax.name}
							value={tValue(tax.amount)}
						/>
					))}
				</Elements.Block>
			)}

			<Separator />

			<Elements.Block aria-label={t('dueAt.label')}>
				<Elements.Item
					title={t('dueAt.booking')}
					value={tValue(price.bookingPrice())}
				/>

				<Elements.Item
					title={t('dueAt.checkIn')}
					value={tValue(price.checkInPrice())}
				/>

				<Elements.Item
					aria-label={t('perDay.label')}
					title={t('perDay.title', { duration: duration })}
					value={tValue(price.pricePerDay(duration, false, false))}
				/>
			</Elements.Block>

			<Separator />

			<Elements.Block aria-label={t('total.label')}>
				{price.discountPrice() > 0 && (
					<>
						<Elements.Item
							className="ts-3"
							aria-label={t('oldPrice.label')}
							title={t('oldPrice.title')}
							value={
								<span className="line-through">
									{tValue(price.totalPrice(false, false))}
								</span>
							}
						/>
						<Elements.Item
							className="ts-3"
							aria-label={t('promocode.label')}
							title={t('promocode.title')}
							value={getDiscountValueForShow(
								tTranslation,
								price.bookingPrice(),
								price.discountPrice(),
								price.discountPercent()
							)}
						/>
					</>
				)}
				<Elements.Item
					className="ts-3 font-semibold"
					title={t('total.title')}
					value={tValue(price.totalPrice())}
				/>
			</Elements.Block>
		</div>
	)
}
